import { useMemo, useRef, useState } from 'react';
import { Copy, Trash2, UserCircle2, Upload, ImageIcon, LayoutGrid } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { uploadFileViaPresignedUrl } from '@/lib/exportMediaStaging';
import { BackgroundTemplatePicker } from '@/components/videoEditor/BackgroundTemplatePicker';
import {
  hostOverlayClipEndTime,
  hostOverlayPlaybackUrl,
  isBackgroundOverlayClip,
} from '@/lib/videoEditorHostOverlays';
import { TimelineLaneMinimizeButton } from '@/components/videoEditor/TimelineLaneMinimizeButton';
import { timelineLaneClipAreaStyle, timelineLaneRowSizeStyle } from '@/lib/videoEditorTimelineLanes';
import { clipsInTimelineViewport } from '@/lib/timelineViewportClips';

function formatTime(sec) {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

function hexToRgba(hex, alpha) {
  const raw = String(hex || '').replace('#', '');
  if (raw.length !== 6) return `rgba(245, 158, 11, ${alpha})`;
  const n = parseInt(raw, 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
}

export function HostOverlayTimelineTrack({
  track,
  trackIdx,
  pxPerSec,
  timelineActive,
  selectedClipId,
  highlightedClipId,
  draggingClipId,
  narrationProjectId,
  storyId,
  onToggleActive,
  onSelectClip,
  onBeginDrag,
  onBeginResize,
  onRemoveClip,
  onDuplicateClip,
  onClipDoubleClick,
  onAddClip,
  onAddBackgroundTemplate,
  onAddCarousel,
  setTrackRef,
  label = 'Host / background',
  emptyHint = 'Add a host photo or pick a background template — drag on preview to position',
  accent = '#f59e0b',
  showHostTools = true,
  testId = 'host-overlay-timeline-track',
  icon: LaneIcon = UserCircle2,
  collapsed = false,
  onToggleCollapsed,
  viewportStartSec = 0,
  viewportEndSec = Number.POSITIVE_INFINITY,
}) {
  const fileRef = useRef(null);
  const [showTemplates, setShowTemplates] = useState(false);
  const visibleClips = useMemo(
    () => clipsInTimelineViewport(track, {
      startSec: viewportStartSec,
      endSec: viewportEndSec,
      forceIds: new Set([selectedClipId, highlightedClipId, draggingClipId].filter(Boolean)),
      getDuration: (clip) => Math.max(
        0.01,
        (Number(hostOverlayClipEndTime(clip)) || 0) - (Number(clip.startTime) || 0),
      ),
    }),
    [track, viewportStartSec, viewportEndSec, selectedClipId, highlightedClipId, draggingClipId],
  );

  const handleUpload = async (file) => {
    if (!file || !file.type.startsWith('image/')) {
      toast.error('Choose an image file for the host photo');
      return;
    }
    try {
      const url = await uploadFileViaPresignedUrl(file, {
        narrationProjectId,
        storyId,
        onProgress: (msg) => toast.info(msg),
      });
      onAddClip(url, file.name.replace(/\.[^.]+$/, '') || 'Host photo');
      toast.success('Host photo added to timeline');
    } catch (err) {
      toast.error(err?.message || 'Upload failed');
    }
  };

  return (
    <div
      className="flex border-b"
      style={{
        borderColor: 'var(--glass-border)',
        opacity: timelineActive ? 1 : 0.45,
        ...timelineLaneRowSizeStyle(collapsed, 56),
      }}
      data-testid={testId}
    >
      <div
        className={`w-36 shrink-0 sticky left-0 z-[100] flex flex-col justify-center gap-1 px-2 border-r shadow-[4px_0_10px_-2px_rgba(0,0,0,0.45)] ${collapsed ? 'py-0 overflow-hidden' : 'py-1.5'}`}
        style={{ background: 'var(--bg-secondary)', borderColor: 'var(--glass-border)', zIndex: 100 }}
      >
        <div className="flex items-center justify-between gap-1 min-w-0">
          <div className="flex items-center gap-1 min-w-0">
            {onToggleCollapsed ? (
              <TimelineLaneMinimizeButton
                collapsed={collapsed}
                onToggle={onToggleCollapsed}
                label={`${label} track`}
                testId={`${testId}-minimize`}
              />
            ) : null}
            <LaneIcon className="w-3 h-3 shrink-0" style={{ color: accent }} />
            <span className="text-[10px] font-medium truncate" style={{ color: 'var(--text-secondary)' }}>
              {label}
            </span>
          </div>
          <button
            type="button"
            onClick={onToggleActive}
            className="text-[10px] rounded px-1.5 py-0.5 border shrink-0"
            style={{
              borderColor: 'var(--glass-border)',
              color: timelineActive ? accent : 'var(--text-tertiary)',
            }}
          >
            {timelineActive ? 'Active' : 'Off'}
          </button>
        </div>
        {showHostTools && !collapsed ? (
          <>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = '';
            if (f) handleUpload(f);
          }}
        />
        <button
          type="button"
          className="text-[9px] rounded border px-1.5 py-1 w-full flex items-center justify-center gap-1"
          style={{ borderColor: 'var(--glass-border)', background: 'var(--bg-primary)', color: 'var(--text-secondary)' }}
          onClick={() => fileRef.current?.click()}
          data-testid="add-host-overlay-btn"
        >
          <Upload className="w-3 h-3" />
          Add photo
        </button>
        <button
          type="button"
          className="text-[9px] rounded border px-1.5 py-1 w-full flex items-center justify-center gap-1"
          style={{ borderColor: 'var(--glass-border)', background: 'var(--bg-primary)', color: 'var(--text-secondary)' }}
          onClick={() => setShowTemplates((v) => !v)}
          data-testid="add-background-template-btn"
        >
          <ImageIcon className="w-3 h-3" />
          Templates
        </button>
        {showTemplates ? (
          <div className="mt-1 p-1.5 rounded border max-h-48 overflow-y-auto" style={{ borderColor: 'var(--glass-border)' }}>
            <BackgroundTemplatePicker
              label="Full-frame background"
              onChange={(templateId) => {
                if (!templateId) return;
                onAddBackgroundTemplate?.(templateId);
                setShowTemplates(false);
                toast.success('Background template added');
              }}
            />
          </div>
        ) : null}
          </>
        ) : null}
        {onAddCarousel && !collapsed ? (
        <button
          type="button"
          className="text-[9px] rounded border px-1.5 py-1 w-full flex items-center justify-center gap-1"
          style={{ borderColor: 'var(--glass-border)', background: 'var(--bg-primary)', color: 'var(--text-secondary)' }}
          onClick={onAddCarousel}
          data-testid="add-card-carousel-btn"
        >
          <LayoutGrid className="w-3 h-3" />
          Cards
        </button>
        ) : null}
      </div>

      <div
        className="relative flex-1 z-0"
        ref={(node) => setTrackRef(trackIdx, node)}
        style={{
          background: trackIdx % 2 === 0 ? 'var(--bg-primary)' : 'var(--bg-tertiary)',
          ...timelineLaneClipAreaStyle(collapsed),
        }}
      >
        {visibleClips.map((clip) => {
          const dur = Math.max(0.1, Number(clip.duration) || 5);
          const end = hostOverlayClipEndTime(clip);
          const selected = selectedClipId === clip.id;
          const highlighted = highlightedClipId === clip.id;
          const dragging = draggingClipId === clip.id;
          const thumb = hostOverlayPlaybackUrl(clip);
          return (
            <motion.div
              key={clip.id}
              className="absolute top-1 bottom-1 rounded-md border flex items-center gap-1 px-1 overflow-hidden cursor-grab active:cursor-grabbing group"
              animate={highlighted ? { scale: [1, 1.04, 1] } : { scale: 1 }}
              transition={highlighted ? { duration: 0.5, repeat: 1 } : { duration: 0.15 }}
              style={{
                left: clip.startTime * pxPerSec,
                width: Math.max(24, dur * pxPerSec),
                background: hexToRgba(accent, highlighted ? 0.5 : 0.28),
                borderColor: selected || highlighted ? accent : hexToRgba(accent, 0.55),
                boxShadow: highlighted
                  ? `0 0 0 3px #ffffff, 0 0 14px 2px ${hexToRgba(accent, 0.9)}`
                  : selected ? `0 0 0 2px ${hexToRgba(accent, 0.35)}` : undefined,
                opacity: clip.muted ? 0.45 : 1,
                zIndex: dragging ? 30 : highlighted ? 25 : selected ? 20 : 10,
              }}
              onMouseDown={(e) => onBeginDrag(e, clip, trackIdx)}
              onClick={(e) => {
                e.stopPropagation();
                onSelectClip(clip);
              }}
              onDoubleClick={(e) => {
                e.stopPropagation();
                onClipDoubleClick?.(clip, trackIdx);
              }}
              title={onClipDoubleClick
                ? 'Double-click to jump the preview here and open prompt, output and size details'
                : undefined}
              data-testid={`host-overlay-clip-${clip.id}`}
            >
              <div
                data-host-resize-edge="left"
                className="absolute left-0 top-0 bottom-0 w-3 cursor-ew-resize z-20"
                title="Drag to trim start"
                onMouseDown={(e) => onBeginResize(e, clip, trackIdx, 'left')}
              />
              {thumb ? (
                <img src={thumb} alt="" className="w-6 h-6 rounded object-cover shrink-0 border border-white/20" />
              ) : (
                <LaneIcon className="w-4 h-4 shrink-0" style={{ color: accent }} />
              )}
              <span className="text-[9px] truncate flex-1 font-medium" style={{ color: accent }}>
                {clip.muted ? '(off) ' : ''}{isBackgroundOverlayClip(clip) ? 'BG: ' : ''}{clip.name || label}
              </span>
              <span className="text-[8px] shrink-0 opacity-70" style={{ color: accent }}>
                {formatTime(clip.startTime)}–{formatTime(end)}
              </span>
              <div className="hidden group-hover:flex items-center gap-0.5 shrink-0">
                <button
                  type="button"
                  className="p-0.5 rounded hover:bg-white/10"
                  title="Duplicate"
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDuplicateClip(clip);
                  }}
                >
                  <Copy className="w-3 h-3" style={{ color: accent }} />
                </button>
                <button
                  type="button"
                  className="p-0.5 rounded hover:bg-white/10"
                  title="Remove"
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemoveClip(clip.id);
                  }}
                >
                  <Trash2 className="w-3 h-3" style={{ color: '#fca5a5' }} />
                </button>
              </div>
              <div
                data-host-resize-edge="right"
                className="absolute right-0 top-0 bottom-0 w-3 cursor-ew-resize z-20"
                title="Drag to trim end"
                onMouseDown={(e) => onBeginResize(e, clip, trackIdx, 'right')}
              />
            </motion.div>
          );
        })}
        {!collapsed && !((track || []).length) ? (
          <div className="absolute inset-0 flex items-center justify-center text-xs px-3 text-center" style={{ color: 'var(--text-tertiary)' }}>
            {emptyHint}
          </div>
        ) : null}
      </div>
    </div>
  );
}
